import { create } from 'zustand';
import type {
  Landmark,
  PresetRun,
  ActiveRun,
  AppSettings,
  AnnouncementSetting,
  UserProfile,
} from '../types';

// ── Settings Store ────────────────────────────────────────────────────────────

interface SettingsState {
  settings: AppSettings;
  setAnnouncement: (key: AnnouncementSetting, value: boolean) => void;
  setProximityRadius: (meters: number) => void;
  setAutopause: (value: boolean) => void;
}

export const useSettingsStore = create<SettingsState>((set) => ({
  settings: {
    announcements: {
      pace: true,
      time: true,
      distance: true,
      first_landmark: true,
      vs_personal_last: true,
      vs_personal_best: true,
      vs_personal_avg: true,
      vs_group_age: false,
      vs_group_gender: false,
      vs_group_named: false,
    },
    proximityRadiusMeters: 20,
    autopause: true,
  },
  setAnnouncement: (key, value) =>
    set((s) => ({
      settings: {
        ...s.settings,
        announcements: { ...s.settings.announcements, [key]: value },
      },
    })),
  setProximityRadius: (meters) =>
    set((s) => ({ settings: { ...s.settings, proximityRadiusMeters: meters } })),
  setAutopause: (value) =>
    set((s) => ({ settings: { ...s.settings, autopause: value } })),
}));

// ── Landmarks Store ───────────────────────────────────────────────────────────

interface LandmarksState {
  landmarks: Landmark[];
  publicLandmarks: Landmark[];
  setLandmarks: (l: Landmark[]) => void;
  setPublicLandmarks: (l: Landmark[]) => void;
  upsertLandmark: (l: Landmark) => void;
  removeLandmark: (id: string) => void;
}

export const useLandmarksStore = create<LandmarksState>((set) => ({
  landmarks: [],
  publicLandmarks: [],
  setLandmarks: (landmarks) => set({ landmarks }),
  setPublicLandmarks: (publicLandmarks) => set({ publicLandmarks }),
  upsertLandmark: (l) =>
    set((s) => {
      const idx = s.landmarks.findIndex((x) => x.landmarkId === l.landmarkId);
      if (idx >= 0) {
        const updated = [...s.landmarks];
        updated[idx] = l;
        return { landmarks: updated };
      }
      return { landmarks: [...s.landmarks, l] };
    }),
  removeLandmark: (id) =>
    set((s) => ({ landmarks: s.landmarks.filter((x) => x.landmarkId !== id) })),
}));

// ── Preset Runs Store ─────────────────────────────────────────────────────────

interface PresetRunsState {
  presetRuns: PresetRun[];
  publicPresetRuns: PresetRun[];
  setPresetRuns: (r: PresetRun[]) => void;
  setPublicPresetRuns: (r: PresetRun[]) => void;
  upsertPresetRun: (r: PresetRun) => void;
  removePresetRun: (id: string) => void;
}

export const usePresetRunsStore = create<PresetRunsState>((set) => ({
  presetRuns: [],
  publicPresetRuns: [],
  setPresetRuns: (presetRuns) => set({ presetRuns }),
  setPublicPresetRuns: (publicPresetRuns) => set({ publicPresetRuns }),
  upsertPresetRun: (r) =>
    set((s) => {
      const idx = s.presetRuns.findIndex((x) => x.presetRunId === r.presetRunId);
      if (idx >= 0) {
        const updated = [...s.presetRuns];
        updated[idx] = r;
        return { presetRuns: updated };
      }
      return { presetRuns: [...s.presetRuns, r] };
    }),
  removePresetRun: (id) =>
    set((s) => ({ presetRuns: s.presetRuns.filter((x) => x.presetRunId !== id) })),
}));

// ── Active Run Store ──────────────────────────────────────────────────────────

interface ActiveRunState {
  activeRun: ActiveRun | null;
  startRun: (run: ActiveRun) => void;
  updateGpx: (lat: number, lng: number, ts: number) => void;
  advanceLandmark: (landmark: Landmark, ts: number) => void;
  addSegment: (segment: import('../types').RunSegment) => void;
  pauseRun: (ts: number) => void;
  resumeRun: (ts: number) => void;
  endRun: () => void;
}

export const useActiveRunStore = create<ActiveRunState>((set) => ({
  activeRun: null,
  startRun: (run) => set({ activeRun: run }),
  updateGpx: (lat, lng, ts) =>
    set((s) => {
      if (!s.activeRun) return s;
      return {
        activeRun: {
          ...s.activeRun,
          gpxPoints: [...s.activeRun.gpxPoints, { lat, lng, ts }],
        },
      };
    }),
  advanceLandmark: (landmark, ts) =>
    set((s) => {
      if (!s.activeRun) return s;
      return {
        activeRun: {
          ...s.activeRun,
          lastPassedLandmark: landmark,
          lastPassedAt: ts,
          nextLandmarkIndex: s.activeRun.nextLandmarkIndex + 1,
          segmentPausedMs: 0, // reset pause tracking for the new segment
        },
      };
    }),
  addSegment: (segment) =>
    set((s) => {
      if (!s.activeRun) return s;
      return { activeRun: { ...s.activeRun, segments: [...s.activeRun.segments, segment] } };
    }),
  pauseRun: (ts) =>
    set((s) => {
      if (!s.activeRun || s.activeRun.pausedAt !== undefined) return s;
      return { activeRun: { ...s.activeRun, pausedAt: ts } };
    }),
  resumeRun: (ts) =>
    set((s) => {
      if (!s.activeRun || s.activeRun.pausedAt === undefined) return s;
      const pausedMs = ts - s.activeRun.pausedAt;
      return {
        activeRun: {
          ...s.activeRun,
          pausedAt: undefined,
          totalPausedMs: s.activeRun.totalPausedMs + pausedMs,
          segmentPausedMs: s.activeRun.segmentPausedMs + pausedMs,
        },
      };
    }),
  endRun: () => set({ activeRun: null }),
}));

// ── User Store ────────────────────────────────────────────────────────────────

interface UserState {
  profile: UserProfile | null;
  setProfile: (p: UserProfile | null) => void;
}

export const useUserStore = create<UserState>((set) => ({
  profile: null,
  setProfile: (profile) => set({ profile }),
}));
